import type { Session } from "@supabase/supabase-js";
import { supabase } from "./supabaseClient";

export function getAuthCallbackParams() {
  const params = new URLSearchParams(window.location.search);
  const hash = window.location.hash.replace(/^#/, "");

  if (hash) {
    new URLSearchParams(hash).forEach((value, key) => {
      if (!params.has(key)) params.set(key, value);
    });
  }

  return params;
}

export function hasAuthCallbackParams(params: URLSearchParams) {
  return (
    params.has("code") ||
    params.has("access_token") ||
    params.has("refresh_token") ||
    params.has("token_hash") ||
    params.has("error") ||
    params.has("error_description")
  );
}

export function clearAuthCallbackParams() {
  window.history.replaceState(null, "", window.location.pathname);
}

export async function resolveAuthSessionFromLocation(options?: {
  allowRecoveryToken?: boolean;
}): Promise<Session | null> {
  if (!supabase) throw new Error("Evo account sign-in is not configured.");

  const params = getAuthCallbackParams();
  const authError = params.get("error_description") || params.get("error");
  if (authError) throw new Error(authError);

  const code = params.get("code");
  if (code) {
    const { data, error } = await supabase.auth.exchangeCodeForSession(code);
    if (error) throw error;
    return data.session;
  }

  const tokenHash = params.get("token_hash");
  const type = params.get("type")?.toLowerCase();
  if (options?.allowRecoveryToken && tokenHash && type === "recovery") {
    const { data, error } = await supabase.auth.verifyOtp({
      token_hash: tokenHash,
      type: "recovery",
    });
    if (error) throw error;
    return data.session;
  }

  const accessToken = params.get("access_token");
  const refreshToken = params.get("refresh_token");
  if (accessToken && refreshToken) {
    const { data, error } = await supabase.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });
    if (error) throw error;
    return data.session;
  }

  const {
    data: { session },
    error,
  } = await supabase.auth.getSession();
  if (error) throw error;
  return session;
}
